import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    Button,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
} from 'grommet';
import { API, PipelineService } from '@enrich/shared-lib';
import { useLoaderData } from 'react-router-dom';
import { EditPipelineButton } from './EditPipelineButton';
import { Trash } from 'grommet-icons';
import { AddPipelineReportRecipientButton } from './AddPipelineReportRecipientButton';
import { PipelineManualReportButton } from './PipelineManualReportButton';

export const PipelineSummary = () => {
    const pipeline = useLoaderData();
    const jobs = pipeline.attributes.jobs?.data;

    const reportRecipientTableStepOptions = [5, 10];
    const reportRecipientsData =
        pipeline.attributes.reportRecipients?.map((r, i) => ({
            id: i + 1,
            email: r,
        })) || [];

    const [reportRecipients, setReportRecipients] =
        useState(reportRecipientsData);
    const [reportRecipientPage, setReportRecipientPage] = useState(1);
    const [totalReportRecipientItems, setTotalReportRecipientItems] =
        useState(0);
    const [reportRecipientPageSize, setReportRecipientPageSize] = useState(
        reportRecipientTableStepOptions[0],
    );

    useEffect(() => {
        if (!pipeline.attributes.isReportingActive || !reportRecipientPage)
            return;

        const pagedReportRecipients = reportRecipientsData.slice(
            reportRecipientPage * reportRecipientPageSize -
                reportRecipientPageSize,
            reportRecipientPage * reportRecipientPageSize,
        );

        setReportRecipientPage(reportRecipientPage);
        setReportRecipients(pagedReportRecipients);
        setTotalReportRecipientItems(reportRecipientsData.length);
    }, [reportRecipientPage, reportRecipientPageSize]);

    const handleReportRecipientPageChange = ({
        page,
        startIndex,
        endIndex,
    }) => {
        const newPageSize = endIndex - startIndex;

        if (reportRecipientPageSize != newPageSize) {
            setReportRecipientPageSize(newPageSize);
            setReportRecipientPage(1);
            return;
        }

        setReportRecipientPage(page);
    };

    const reportRecipientDataColumns = [
        {
            header: 'Email',
            size: 'large',
            property: 'email',
            sortable: true,
        },
        {
            header: 'Action',
            size: 'small',
            property: 'id',
            primary: true,
            render: (recipient) => {
                const [buttonBusy, setButtonBusy] = useState();

                return (
                    <Button
                        pad={'none'}
                        color={'enrich-red'}
                        size="small"
                        plain
                        busy={buttonBusy}
                        label=<Trash
                            size="medium"
                            color="red"
                            onClick={async () => {
                                setButtonBusy(true);

                                let reportRecipients = reportRecipientsData
                                    .filter((x) => x.email !== recipient.email)
                                    .map((x) => x.email);

                                await new PipelineService().update(
                                    pipeline.id,
                                    {
                                        reportRecipients,
                                    },
                                );

                                setButtonBusy(false);
                                location.reload();
                            }}
                        />
                    />
                );
            },
            sortable: false,
        },
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap="medium">
                <Box direction="row-responsive" gap={{ column: 'medium' }}>
                    <Box>
                        <Heading level="2">{pipeline.attributes.name}</Heading>
                    </Box>

                    <Box alignSelf="center">
                        <EditPipelineButton pipeline={pipeline} />
                    </Box>

                    <Box alignSelf="center">
                        <PipelineManualReportButton pipeline={pipeline} />
                    </Box>
                </Box>

                {pipeline.attributes.isReportingActive == true && (
                    <Box direction="row-responsive" fill="horizontal">
                        <Box basis="large" fill="horizontal">
                            <Box
                                direction="row-responsive"
                                fill="horizontal"
                                pad={{ right: 'small' }}
                                gap="medium">
                                <Box basis="large" fill="horizontal">
                                    <Heading level="3">
                                        Report Recipients
                                    </Heading>
                                </Box>
                                <Box
                                    basis="small"
                                    justify="center"
                                    fill="horizontal">
                                    <AddPipelineReportRecipientButton
                                        pipelineId={pipeline.id}
                                        // eslint-disable-next-line max-len
                                        recipients={reportRecipientsData.map(
                                            (x) => x.email,
                                        )}
                                    />
                                </Box>
                            </Box>

                            <Box
                                direction="row-responsive"
                                elevation="large"
                                pad="medium"
                                fill="horizontal">
                                <DataTable
                                    sortable
                                    data={reportRecipients}
                                    columns={reportRecipientDataColumns}
                                    fill="horizontal"
                                />
                            </Box>

                            <Box
                                align="center"
                                justify="center"
                                margin={{ top: 'medium', bottom: 'none' }}>
                                <Pagination
                                    numberItems={totalReportRecipientItems}
                                    onChange={({
                                        page,
                                        startIndex,
                                        endIndex,
                                    }) => {
                                        handleReportRecipientPageChange({
                                            page,
                                            startIndex,
                                            endIndex,
                                        });
                                    }}
                                    size="small"
                                    step={reportRecipientPageSize}
                                    stepOptions={
                                        reportRecipientTableStepOptions
                                    }
                                    summary={true}
                                />
                            </Box>
                        </Box>
                    </Box>
                )}
                <Box>
                    <Heading level="3">Jobs</Heading>
                    <Box elevation="medium" pad="medium">
                        {jobs.map((job) => (
                            <Anchor
                                key={job.id}
                                href={`/jobs/${job.id}`}
                                label={job.attributes.title}
                            />
                        ))}
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

export async function loader({ params }) {
    const pipelineQuery = `query getPipeline($pipelineId: ID!) {
        pipeline(id: $pipelineId) {
            data {
                id
                attributes {
                    name
                    reportRecipients
                    isReportingActive
                    company {
                        data {
                            id
                            attributes {
                                name                                
                            }
                        }
                    }
                    jobs {
                        data {
                            id
                            attributes {
                                title
                            }
                        }
                    }
                }
            }
        }
    }`;

    try {
        const response = await API.post('graphql', {
            query: pipelineQuery,
            variables: { pipelineId: params.pipelineId },
        });

        return response.data.data.pipeline.data;
    } catch (error) {
        console.log(error);
        return {};
    }
}
